/**
 * Adding the app to a home screen, so it opens full-screen like an app
 * (decided with Tiago 11 October, ahead of the native apps). Chrome on Android
 * offers it by an event, `beforeinstallprompt`, which fires once and early:
 * the page keeps it, quiets Chrome's own bar, and offers Install on Home. An
 * iPhone's Safari has no such event — only Share, then Add to Home Screen —
 * which the page can only describe.
 */

type InstallEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

let kept: InstallEvent | null = null;
const listeners = new Set<() => void>();
const changed = () => listeners.forEach((listener) => listener());

/** From the moment the app starts, since the event fires once and early. */
export function listenForInstall(target: Window = window): void {
  target.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    kept = event as InstallEvent;
    changed();
  });
  target.addEventListener("appinstalled", () => {
    kept = null;
    changed();
  });
}

/** Tells a component when installing becomes possible, or is done. */
export function onInstallChange(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Whether the app is open from a home screen already. */
export function isStandalone(): boolean {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches === true ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

/** Whether a tap can open the browser's own install dialog now. */
export function canPrompt(): boolean {
  return kept !== null;
}

/** Opens it: true when they installed. The event is spent either way. */
export async function promptInstall(): Promise<boolean> {
  const event = kept;
  if (!event) return false;
  kept = null;
  await event.prompt();
  const { outcome } = await event.userChoice;
  changed();
  return outcome === "accepted";
}

export type InstallHint = "prompt" | "ios" | "none";

/**
 * What a page can offer: the browser's dialog behind a button, an iPhone's or
 * iPad's steps, or nothing — open from a home screen already, or a browser
 * that offers neither. An iPad says it is a Mac, and gives itself away by
 * touch.
 */
export function installHint({
  standalone,
  userAgent,
  touchPoints,
  prompt,
}: {
  standalone: boolean;
  userAgent: string;
  touchPoints: number;
  prompt: boolean;
}): InstallHint {
  if (standalone) return "none";
  if (prompt) return "prompt";
  if (/iPhone|iPad|iPod/.test(userAgent) || (/Macintosh/.test(userAgent) && touchPoints > 1)) return "ios";
  return "none";
}
